import { useEffect, useState, FC } from 'react'
import { Grid, Typography, Stack, Alert, Link, Box } from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import { useAppSelector, useGoogleAnalytics } from '../../../../hooks'
import CustomNumberField from '../profile/CustomNumberField'
import { EVENTS } from '../../../../constants/googleEvents'
import OTPTracker from '../../../../analytics/Trackers/OTPTracker'

type LoginMethod = 'mobile' | 'email'

type VerifyOTPProps = {
  verifyOtpLoading: boolean
  fieldWidth: number
  otpHandler: (otp: string) => void
  resendOTPHandler: () => void
  onBackToLogin?: () => void
  apiError?: string
  expirationInSeconds: number
  loginMethod: LoginMethod
  identifier: string
}

function maskEmail(email: string): string {
  const atIdx = email.indexOf('@')
  if (atIdx <= 0) return email
  const local = email.slice(0, atIdx)
  const domain = email.slice(atIdx)
  const visible = local.slice(0, 1)
  return `${visible}***${domain}`
}

function formatPhone(phone: string): string {
  // phone is stored as +91XXXXXXXXXX
  if (phone.startsWith('+91') && phone.length === 13) {
    const digits = phone.slice(3)
    return `+91 ${digits.slice(0, 5)}-${digits.slice(5)}`
  }
  return phone
}

const VerifyOTP: FC<VerifyOTPProps> = ({
  otpHandler,
  resendOTPHandler,
  onBackToLogin,
  fieldWidth,
  apiError,
  loginMethod,
  identifier,
}) => {
  const { loading, success, message } = useAppSelector(
    (state) => state.userData
  )
  const analytics = useGoogleAnalytics()
  const otpTracker = new OTPTracker()

  const [seconds, setSeconds] = useState(25)
  const [otp, setOTP] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState(null)

  const showReduxMessageAlert =
    Boolean(message) &&
    !String(message).toLowerCase().includes('wrong')

  useEffect(() => {
    const interval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1)
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [seconds])

  useEffect(() => {
    setErrorMessage(apiError)
  }, [apiError])

  const otpHandle = () => {
    if (otp?.length == 6) {
      setErrorMessage(null)
      analytics(
        EVENTS.login.submitOtpClicked.eventName,
        EVENTS.login.submitOtpClicked.eventParams
      )
      otpTracker.trackSubmitOtpButton()
      otpHandler(otp)
    } else {
      setErrorMessage('Please enter 6 digit code')
      otpTracker.trackValidationError(
        '/user/verify_otp',
        'Please enter 6 digit code'
      )
    }
  }

  const resendOTP = () => {
    setErrorMessage(null)
    setSeconds(25)
    resendOTPHandler()
  }

  const maskedIdentifier =
    loginMethod === 'email' ? maskEmail(identifier) : formatPhone(identifier)

  const changeLabel = loginMethod === 'email' ? 'Change Email' : 'Change Number'

  return (
    <Grid
      container={true}
      spacing={3}
      direction="column"
      alignItems="center"
      sx={{ py: 5 }}
    >
      <Grid item>
        <Box
          sx={{
            width: fieldWidth,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            component="h6"
            variant="h6"
            align="center"
            noWrap
            sx={{ width: '100%' }}
          >
            We&apos;ve sent a code to <strong>{maskedIdentifier}</strong>
          </Typography>
        </Box>
      </Grid>
      {!loading && !success && showReduxMessageAlert && (
        <Grid item>
          <Stack sx={{ width: '100%' }} spacing={2}>
            <Alert severity="error">{message}</Alert>
          </Stack>
        </Grid>
      )}

      <Grid item xs>
        <CustomNumberField
          name="text"
          value={otp}
          label="OTP"
          maxLength={6}
          error={errorMessage}
          required
          helperText={errorMessage}
          onChange={(e) => setOTP(e.target.value)}
          sx={{ width: fieldWidth }}
        />
      </Grid>

      <Grid item xs>
        <LoadingButton
          onClick={otpHandle}
          sx={{ width: fieldWidth, height: 52, mt: 0 }}
          variant={loading ? 'outlined' : 'contained'}
          color="primary"
          size="large"
          loading={loading}
        >
          Submit
        </LoadingButton>
      </Grid>
      <Grid item xs>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          {seconds === 0 ? (
            <Typography component="h6" variant="h6" color="red">
              <Link href="#" onClick={resendOTP} underline="always">
                Resend OTP
              </Link>
            </Typography>
          ) : (
            <Typography component="h6" variant="h6">
              Time left to resend OTP: {seconds}
            </Typography>
          )}
          {onBackToLogin && (
            <>
              <Typography
                component="span"
                variant="h6"
                sx={{ color: 'text.disabled' }}
              >
                |
              </Typography>
              <Typography component="h6" variant="h6">
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    setErrorMessage(null)
                    onBackToLogin()
                  }}
                  underline="always"
                >
                  {changeLabel}
                </Link>
              </Typography>
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

export default VerifyOTP
